import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getMessages, getTwilioNumberFromAPI, getUnreadMessages, markAsRead, sendReply } from '../api/api';
import { io } from 'socket.io-client';

const MessageContext = createContext();

export function MessageProvider({ children }) {
    const [threads, setThreads] = useState([]);
    const [messages, setMessages] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [selectedMessage, setSelectedMessage] = useState(null);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [socket, setSocket] = useState(null);
    const [initialLoad, setInitialLoad] = useState(false);
    // Memoized fetch function
    const fetchMessages = useCallback(async (force = false) => {

        // Skip if already loading or not forced
        if (loading && !force) return;

        try {
            setLoading(true);
            const [allMessages, unreadMessages] = await Promise.all([
                getMessages(),
                getUnreadMessages()
            ]);
            // setMessages(allMessages);
            setThreads(allMessages);
            setUnreadCount(unreadMessages.length);
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to load messages');
        } finally {
            setLoading(false);
        }
    }, []); // Keep loading as dependency

    // Initialize WebSocket connection
    useEffect(() => {
        const newSocket = io(process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000', {
            withCredentials: true,
            // transports: ['websocket'], // Force WebSocket only
            reconnectionAttempts: 3,
            reconnectionDelay: 1000,
            timeout: 20000, // Increased timeout
            autoConnect: false, // Manually connect after setup
            path: "/socket.io"
        });

        newSocket.on('connect', () => {
            console.log('Connected to WebSocket server');
            setSocket(newSocket);
        });

        newSocket.on('disconnect', () => {
            console.log('Disconnected from WebSocket server');
        });

        newSocket.on('newMessage', (message) => {
            handleIncomingMessage(message);
            setUnreadCount(prev => prev + 1);
        });

        newSocket.on('status-update', (message) => {
            handleStatusUpdate(message);
        });

        newSocket.connect();

        const loadInitialData = async () => {
            await fetchMessages(true); // Force initial load
        };
        loadInitialData();
        // // Initial data load
        // if (!initialLoad) {
        //     fetchMessages();
        //     setInitialLoad(true);
        // }

        return () => {
            newSocket.disconnect();
        };
    }, [fetchMessages]);

    const handleIncomingMessage = useCallback((newMessage) => {
        setThreads(prevThreads => {
            // Check if the message belongs to an existing thread
            const threadIndex = prevThreads.findIndex(thread =>
                thread.from_number === newMessage.from_number
            );

            if (threadIndex !== -1) {
                prevThreads[threadIndex].created_at = newMessage.created_at;
                prevThreads[threadIndex].body = newMessage.body;
                prevThreads[threadIndex].status = 'unread';
                // Update existing thread
                const updatedThread = {
                    ...prevThreads[threadIndex],
                    conversation: [
                        ...prevThreads[threadIndex].conversation,
                        newMessage
                    ]
                };

                const updatedThreads = [...prevThreads];
                updatedThreads[threadIndex] = updatedThread;

                return updatedThreads;
            } else {
                // Create new thread
                return [
                    {
                        from_number: newMessage.from_number,
                        id: newMessage.id, // optional: you might have another id
                        body: newMessage.body,
                        status: 'unread',
                        created_at: newMessage.created_at,
                        direction: "inbound",
                        conversation: [newMessage]
                    },
                    ...prevThreads // add on top if you want latest first
                ];
            }

        });
    }, []);

    const handleStatusUpdate = useCallback((message) => {

        setThreads(prevThreads => {
            return prevThreads.map(thread => {
                // Find message inside this thread
                const updatedConversation = thread.conversation.map(msg =>
                    msg.id === message.id
                        ? { ...msg, status: message.status }
                        : msg
                );

                // Return updated thread
                return {
                    ...thread,
                    conversation: updatedConversation
                };
            });
        });
        setSelectedMessage(prev => {
            if (!prev) return prev;

            const updatedConversation = prev.conversation.map(msg =>
                msg.id === message.id
                    ? { ...msg, status: message.status }
                    : msg
            );

            return {
                ...prev,
                conversation: updatedConversation
            };
        });
    }, []);

    // Rest of your context methods remain the same...
    const handleMarkAsRead = useCallback(async (messageId) => {
        try {
            const updatedMessage = await markAsRead(messageId);

            setThreads(prevThreads =>
                prevThreads.map(thread => {
                    if (thread.id === messageId && thread.status !== 'read') {
                        return {
                            ...thread,
                            status: 'read',
                            conversation: thread.conversation.map(msg =>
                                msg.status === 'unread' ? { ...msg, status: 'read' } : msg
                            )
                        };
                    }
                    return thread;
                })
            );
            setUnreadCount(prev => prev - 1);
        } catch (err) {
            console.error('Failed to mark message as read:', err);
        }
    }, []);

    const handleReply = useCallback(async (from, to, body, refId) => {

        try {
            const sentMessage = await sendReply(from, to, body, refId);

            setThreads(prevThreads => {
                const threadIndex = prevThreads.findIndex(a => a.id === refId);
                if (threadIndex === -1) return prevThreads;
                const updatedThread = {
                    ...prevThreads[threadIndex],
                    conversation: [
                        ...prevThreads[threadIndex].conversation,
                        sentMessage
                    ]
                };
                setSelectedMessage(updatedThread);
                const updatedThreads = [...prevThreads];
                updatedThreads[threadIndex] = updatedThread;
                return updatedThreads;
            });
            return { success: true };
        } catch (err) {
            return { success: false, error: err.message || 'Failed to send reply' };
        }
    }, []);

    const getTwilioNumber = useCallback(async () => {
        return getTwilioNumberFromAPI();
    }, []);



    const value = {
        messages,
        messageThreads: threads,
        unreadCount,
        selectedMessage,
        setSelectedMessage,
        loading,
        error,
        fetchMessages,
        markAsRead: handleMarkAsRead,
        sendReply: handleReply,
        getTwilioNumber
    };

    return (
        <MessageContext.Provider value={value}>
            {children}
        </MessageContext.Provider>
    );
}

export function useMessages() {
    return useContext(MessageContext);
}